import React, { Component } from 'react';
import { Button } from '@trucktrax/trucktrax-common';
import { PlantDto, ProductLine } from '@trucktrax/trucktrax-ts-common';

import Autocomplete from '../../forms/Autocomplete';
import { INVALID_STALE_TOKEN_LOGOUT } from '../../../../constants/appConstants';
import { getPlantList } from '../../../../services/plantsService';
import { DivertPlantOption, toDivertPlantOptions } from '../../../../util/deliveryLocationUtil';
import styles from './DivertPlantPicker.module.css';
import {
  DELIVERY_LOCATION_MAX_PLANT_SUGGESTIONS,
  DELIVERY_LOCATION_PLANT_HINT,
  DELIVERY_LOCATION_PLANT_LIST_RETRY,
  DELIVERY_LOCATION_PLANT_LOADING,
} from '../../../../constants/deliveryLocationConstants';
import { ERROR_TEXT_FETCH_PLANT_LIST } from '../../../../constants/errorConstants';

// The single source of truth for what the picker is showing. Collapses what were two
// correlated booleans (loading/failed) into one phase, so `loading && failed` stops being
// constructible and every render branch is reachable. Mirrors AddressPhase in the sibling
// DeliveryAddressPicker, which models this modal's other mode the same way.
type PlantListPhase =
  | { kind: 'loading' }
  | { kind: 'loaded'; plants: DivertPlantOption[] }
  | { kind: 'failed' };

export interface DivertPlantPickerProps {
  ticketProductLine?: ProductLine | null;
  // Fired when the dispatcher picks a plant from the list.
  onSelected: (plant: DivertPlantOption) => void;
  // Fired when the search field is cleared, so the parent abandons a prior selection.
  onCleared: () => void;
  // Surface a plant-list fetch failure (wired to a snackbar by the parent).
  onError: (message: string) => void;
}

interface DivertPlantPickerState {
  searchText: string;
  phase: PlantListPhase;
}

export class DivertPlantPicker extends Component<DivertPlantPickerProps, DivertPlantPickerState> {
  // componentDidMount fetches unconditionally, so the picker is already loading before its
  // first render — starting anywhere else would paint the idle hint for one commit.
  state: DivertPlantPickerState = { searchText: '', phase: { kind: 'loading' } };

  // The fetch below resolves after unmount if the dispatcher closes the modal, or switches
  // to address mode, before it lands.
  private isUnmounted = false;

  componentDidMount(): void {
    void this.loadPlants();
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
  }

  // Fetch the tenant's plants without a region query; the product-line matrix is applied
  // locally so a Cement ticket offers Cement/ReadyMix and an Aggregates ticket offers
  // Aggregates/ReadyMix destinations.
  //
  // getPlantList propagates rather than reporting failure as an empty list, so a dead
  // request is visibly distinct from a tenant with no plants.
  private loadPlants = async (): Promise<void> => {
    this.setState({ phase: { kind: 'loading' } });
    try {
      const plants: PlantDto[] = await getPlantList();
      if (this.isUnmounted) {
        return;
      }
      this.setState({
        phase: {
          kind: 'loaded',
          plants: toDivertPlantOptions(plants, this.props.ticketProductLine),
        },
      });
    } catch (e) {
      if (this.isUnmounted || e === INVALID_STALE_TOKEN_LOGOUT) {
        // Session expiry, not a plants failure — the app is already logging out; don't
        // surface a "failed to fetch plant list" error over it. Matches the other
        // getPlantList call sites.
        return;
      }
      this.setState({ phase: { kind: 'failed' } });
      this.props.onError(ERROR_TEXT_FETCH_PLANT_LIST);
    }
  };

  private handleSelected = (plant: DivertPlantOption): void => {
    // Autocomplete fires onNewRequest with a raw string when the user types + presses Enter;
    // only act on an actual selected plant object.
    if (!plant || typeof plant !== 'object') {
      return;
    }
    // Echo the labelled form back into the input so it matches the row that was picked.
    this.setState({ searchText: plant.displayName ?? plant.name ?? '' });
    this.props.onSelected(plant);
  };

  private handleCleared = (): void => {
    this.setState({ searchText: '' });
    this.props.onCleared();
  };

  private renderError(): JSX.Element | null {
    if (this.state.phase.kind !== 'failed') {
      return null;
    }
    // The snackbar that fired on failure is transient and an empty dropdown looks the same
    // as a tenant with no plants, so say so in place — and offer the only recovery there
    // is, since nothing else retriggers the fetch. role=alert because the snackbar is gone
    // by the time a screen reader would arrive and this row appears silently.
    return (
      <div
        className={styles.plantListError}
        data-test="delivery-location-plant-list-error"
        role="alert"
      >
        <span>{ERROR_TEXT_FETCH_PLANT_LIST}</span>
        <Button
          name={DELIVERY_LOCATION_PLANT_LIST_RETRY}
          dataTest="delivery-location-plant-list-retry"
          buttonClassName="tt-btn-secondary"
          onClick={() => {
            void this.loadPlants();
          }}
        />
      </div>
    );
  }

  render(): JSX.Element {
    const { phase, searchText } = this.state;
    return (
      <div className={styles.root} data-test="delivery-location-plant-mode">
        <Autocomplete
          dataTest="delivery-location-plant-autocomplete"
          // The plant list arrives in one request after mount, so the dropdown is briefly
          // empty for legitimate reasons. Say which reason it is.
          hintText={
            phase.kind === 'loading'
              ? DELIVERY_LOCATION_PLANT_LOADING
              : DELIVERY_LOCATION_PLANT_HINT
          }
          searchText={searchText}
          dataSource={phase.kind === 'loaded' ? phase.plants : []}
          dataSourceConfig={{ text: 'displayName', value: 'url' }}
          onUpdateInput={(value: string) => this.setState({ searchText: value })}
          onNewRequest={(plant: DivertPlantOption) => this.handleSelected(plant)}
          clearSearch={this.handleCleared}
          openOnFocus
          showAll
          autocompleteHeight="300px"
          // The destination list can contain many plants, so Autocomplete's default of
          // 5 rows is too small a sample for this 300px scroll area.
          maxSuggestions={DELIVERY_LOCATION_MAX_PLANT_SUGGESTIONS}
          maxLength={-1}
        />
        {this.renderError()}
      </div>
    );
  }
}

export default DivertPlantPicker;
